import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { safeRedirectPath } from "@/lib/safe-redirect";
import Image from "next/image";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";

import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useUser } from "@/providers/user-provider";
import { useUi } from "@/providers/ui-provider";
import FormInput from "@/components/form/input";
import { createClient } from "@/lib/supabase/component";
import {
  getDeviceIdentificationState,
  isLoginDeviceGateEnabled,
  type DeviceIdentificationState,
} from "@/lib/device-identification";
import { BrowserCompatDialog } from "@/components/auth/browser-compat-dialog";
import { notifyClient } from "@/lib/bugsnag/client";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@/components/auth/turnstile-widget";
import { isTurnstileEnabled, CAPTCHA_UNAVAILABLE_MESSAGE } from "@/lib/turnstile/config";
import { TotpCodeInput } from "@/components/ui/totp-code-input";
import { getVerifiedTotpFactorId, type MfaFactor } from "@/lib/mfa/factors";
import { postLoginStep } from "@/lib/auth/login-flow";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const REASON_MESSAGES: Record<string, string> = {
  deactivated: "Conta desativada. Contate o administrador.",
  device_blocked:
    "Acesso negado. Este dispositivo foi bloqueado. Contate o administrador.",
};

const LOGIN_DEVICE_BLOCKED_EVENT = "login_device_id_blocked";
const LOGIN_CAPTCHA_UNAVAILABLE_EVENT = "login_captcha_unavailable";

export default function LoginPage() {
  const router = useRouter();
  const { logIn } = useUser();
  const { setMobileMenuOpen } = useUi();
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const captchaRequired = isTurnstileEnabled();
  const { data, error, isLoading, getData } = useVisitorData(
    { extendedResult: true },
    { immediate: true }
  );
  const [retrying, setRetrying] = useState(false);
  const [compatOpen, setCompatOpen] = useState(false);
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
  const reportedRef = useRef(false);
  const captchaUnavailableReportedRef = useRef(false);
  const [totpStep, setTotpStep] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const enforce = isLoginDeviceGateEnabled();
  const rawDeviceState = getDeviceIdentificationState({
    visitorId: data?.visitorId,
    error,
    isLoading: Boolean(isLoading),
  });
  const deviceState: DeviceIdentificationState = enforce
    ? rawDeviceState
    : "ok";

  // Reporta ao Bugsnag uma vez quando bloquear (métrica de incidência).
  useEffect(() => {
    if (deviceState === "blocked" && !reportedRef.current) {
      reportedRef.current = true;
      notifyClient(new Error(LOGIN_DEVICE_BLOCKED_EVENT), {
        context: LOGIN_DEVICE_BLOCKED_EVENT,
        fingerprintError:
          error instanceof Error ? error.message : error ? String(error) : null,
      });
    } else if (deviceState !== "blocked") {
      reportedRef.current = false;
    }
  }, [deviceState, error]);

  // Widget travou (script bloqueado, browser não suportado, ou timeout sem
  // token). Mostra a mensagem sempre; reporta ao Bugsnag uma vez só, mesmo
  // se onUnavailable disparar de novo (ex: script error + timeout guard).
  const handleCaptchaUnavailable = () => {
    setCaptchaUnavailable(true);
    if (!captchaUnavailableReportedRef.current) {
      captchaUnavailableReportedRef.current = true;
      notifyClient(new Error(LOGIN_CAPTCHA_UNAVAILABLE_EVENT), {
        context: LOGIN_CAPTCHA_UNAVAILABLE_EVENT,
      });
    }
  };

  // Token chegou (ex: usuário resolveu o desafio interativo depois do
  // timeout falso-positivo) — zera a flag pra mensagem e label do botão
  // nunca ficarem contraditórios com o estado real.
  const handleCaptchaToken = (token: string | null) => {
    setCaptchaToken(token);
    if (token) setCaptchaUnavailable(false);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const resolved = await getData({ ignoreCache: true });
      const state = getDeviceIdentificationState({
        visitorId: resolved?.visitorId,
        error: undefined,
        isLoading: false,
      });
      // Resolveu: fecha a modal e libera o usuário pra clicar "Entrar".
      if (state === "ok") setCompatOpen(false);
    } catch {
      // Segue bloqueado — a modal permanece aberta.
    } finally {
      setRetrying(false);
    }
  };

  // Trata redirect com motivo (deactivated, device_blocked) — força signOut e mostra toast
  useEffect(() => {
    const reason = router.query.reason as string | undefined;
    if (reason && REASON_MESSAGES[reason]) {
      const supabase = createClient();
      supabase.auth.signOut().finally(() => {
        toast.error(REASON_MESSAGES[reason]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.reason]);

  // Middleware devolveu pra cá uma sessão aal1 com fator pendente (tentativa
  // de navegar sem confirmar o código) — retoma direto no passo TOTP.
  useEffect(() => {
    if (router.query.reason !== "totp_required") return;
    const supabase = createClient();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setTotpStep(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.reason]);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: LoginValues) => {
    if (enforce) {
      let state = deviceState;
      // Se ainda identificando no clique, espera o getData resolver.
      if (state === "identifying") {
        try {
          const resolved = await getData();
          state = getDeviceIdentificationState({
            visitorId: resolved?.visitorId,
            error: undefined,
            isLoading: false,
          });
        } catch {
          state = "blocked";
        }
      }
      if (state === "blocked") {
        // Abre a modal genérica de compatibilidade e não tenta logar.
        setCompatOpen(true);
        return;
      }
    }

    setLoading(true);
    const result = await logIn(values.email, values.password, captchaToken ?? undefined);
    if (!result.ok) {
      setLoading(false);
      // Token é de uso único: sem reset, a 2ª tentativa falha por captcha
      // mesmo com a senha certa.
      turnstileRef.current?.reset();
      return;
    }

    const step = postLoginStep(result.role, result.hasTotp === true);
    const redirectParam = router.query.redirect ? safeRedirectPath(router.query.redirect) : undefined;

    if (step === "totp") {
      // Sessão está aal1: o middleware/APIs bloqueiam até o verify. A página
      // troca pro passo do código sem redirect.
      setLoading(false);
      setTotpStep(true);
      return;
    }

    toast.success("Login realizado com sucesso");
    setMobileMenuOpen(false);

    // logIn() já popula user+client no provider — o updateUser() extra aqui
    // custava getUser+clients (2 roundtrips) segurando o redirect (~500ms).

    if (step === "setup") {
      // Interstitial obrigatória de cliente sem 2FA — em TODO login.
      router.replace(
        redirectParam
          ? `/configurar-2fa?redirect=${encodeURIComponent(redirectParam)}`
          : "/configurar-2fa",
      );
      return;
    }

    router.replace(redirectParam ?? "/");
  };

  const submitTotp = async () => {
    setLoading(true);
    const supabase = createClient();
    const { data: factorsData, error: listErr } =
      await supabase.auth.mfa.listFactors();
    const factorId = listErr
      ? null
      : getVerifiedTotpFactorId((factorsData?.all ?? []) as MfaFactor[]);
    if (!factorId) {
      setLoading(false);
      toast.error("Não foi possível validar o 2FA. Tente de novo.");
      return;
    }
    const challenge = await supabase.auth.mfa.challenge({ factorId });
    if (challenge.error || !challenge.data) {
      setLoading(false);
      toast.error("Falha ao verificar o código. Tente de novo.");
      return;
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code: totpCode,
    });
    setLoading(false);
    if (error) {
      toast.error("Código inválido. Tente de novo.");
      setTotpCode("");
      return;
    }
    // Sessão agora é aal2 — o gate libera. (O SIGNED_IN do verify é ignorado
    // pelo UserProvider: mesmo userId já carregado.)
    toast.success("Login realizado com sucesso");
    setMobileMenuOpen(false);
    const redirectParam = router.query.redirect ? safeRedirectPath(router.query.redirect) : undefined;
    router.replace(redirectParam ?? "/");
  };

  const exitTotpStep = async () => {
    const supabase = createClient();
    await supabase.auth.signOut(); // sessão aal1 pendente não pode sobrar
    setTotpStep(false);
    setTotpCode("");
    // Token do Turnstile é de uso único — o form de senha precisa de um novo.
    turnstileRef.current?.reset();
  };

  return (
    <div className="min-h-dvh w-full flex items-center justify-center bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0a1a4a] to-[#040b1f] px-4">
      <div className="w-full max-w-7xl min-h-[75vh] grid grid-cols-1 md:grid-cols-2 gap-0 rounded-2xl border bg-white overflow-hidden shadow-lg">
        <div className="bg-background p-8 md:p-10 flex flex-col justify-center min-h-[75vh]">
          <div className="mb-6 flex items-center justify-center md:hidden">
            <Image
              src="/imgs/logo-background.svg"
              alt="ViOux"
              width={203}
              height={194}
              className="h-40 w-auto"
              priority
            />
          </div>
          <h1 className="text-2xl font-semibold mb-8">
            {totpStep ? "Confirme seu código" : "Login"}
          </h1>
          {totpStep ? (
            <div className="grid gap-4">
              <p className="text-sm text-muted-foreground">
                Digite o código de 6 dígitos do seu aplicativo autenticador
                para concluir o login.
              </p>
              <TotpCodeInput value={totpCode} onChange={setTotpCode} />
              <Button
                onClick={submitTotp}
                disabled={loading || totpCode.length !== 6}
                className="mt-2"
              >
                {loading ? "Validando…" : "Confirmar código"}
              </Button>
              <Button variant="ghost" onClick={exitTotpStep} disabled={loading}>
                Entrar com outra conta
              </Button>
            </div>
          ) : (
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid gap-4"
              noValidate
            >
              <FormInput
                name="email"
                control={form.control}
                label="Email"
                type="email"
                placeholder="Digite seu e-mail"
              />
              <FormInput
                name="password"
                control={form.control}
                label="Senha"
                type="password"
                placeholder="Digite sua senha"
              />
              <div className="text-right">
                <Link
                  href="#"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Recuperar senha
                </Link>
              </div>
              <TurnstileWidget
                ref={turnstileRef}
                onToken={handleCaptchaToken}
                onUnavailable={handleCaptchaUnavailable}
                className="mt-1"
              />
              {captchaUnavailable ? (
                <p className="text-xs text-red-600" role="alert">
                  {CAPTCHA_UNAVAILABLE_MESSAGE}
                </p>
              ) : null}
              <Button
                type="submit"
                disabled={loading || (captchaRequired && !captchaToken)}
                className="mt-2"
              >
                {loading
                  ? "Entrando..."
                  : captchaUnavailable
                    ? "Entrar"
                    : captchaRequired && !captchaToken
                      ? "Verificando..."
                      : "Entrar"}
              </Button>
            </form>
          )}
        </div>
        <div className="relative hidden md:block min-h-[75vh]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#071238] via-[#08153f] to-[#030a20] rounded-[2rem] m-5" />
          <div className="relative h-full w-full flex items-center justify-center p-8">
            <Image
              src="/imgs/logo.svg"
              alt="ViOux"
              width={200}
              height={120}
              className="h-24 w-auto drop-shadow-xl"
              priority
            />
          </div>
        </div>
      </div>
      <BrowserCompatDialog
        open={compatOpen}
        onOpenChange={setCompatOpen}
        onRetry={handleRetry}
        retrying={retrying}
      />
    </div>
  );
}
